import type { EventListItem } from '../../api/types';

// 권위: docs/ARCH_DASHBOARD.md §4.8 본체 이벤트 테이블 — 컬럼 + 색상 규약 + 클릭 → 상세.

interface Props {
  items: EventListItem[];
  isLoading: boolean;
  onRowClick: (item: EventListItem) => void;
  realtimeCount: number;
}

export function SystemLogTable({ items, isLoading, onRowClick, realtimeCount }: Props): JSX.Element {
  return (
    <div className="syslog-table-wrap">
      {realtimeCount > 0 && (
        <div className="syslog-table__realtime-note" aria-live="polite">
          실시간 수신 {realtimeCount}건 (상단)
        </div>
      )}
      <table className="syslog-table">
        <thead>
          <tr>
            <th className="syslog-col-time">발생 시각 (KST)</th>
            <th className="syslog-col-sev">Severity</th>
            <th className="syslog-col-source">Source</th>
            <th className="syslog-col-code">Event code</th>
            <th className="syslog-col-summary">Summary</th>
            <th className="syslog-col-ack">Ack</th>
          </tr>
        </thead>
        <tbody>
          {isLoading && items.length === 0 && (
            <tr>
              <td colSpan={6} className="syslog-empty">
                불러오는 중…
              </td>
            </tr>
          )}
          {!isLoading && items.length === 0 && (
            <tr>
              <td colSpan={6} className="syslog-empty">
                조건에 맞는 이벤트가 없습니다.
              </td>
            </tr>
          )}
          {items.map((it, idx) => {
            const sev = it.severity.toLowerCase();
            const isRealtime = idx < realtimeCount;
            const cls = `syslog-row syslog-row--${sev}${isRealtime ? ' syslog-row--realtime' : ''}`;
            return (
              <tr
                key={it.eventId}
                className={cls}
                onClick={() => onRowClick(it)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onRowClick(it);
                  }
                }}
              >
                <td className="syslog-col-time">{formatTs(it.occurredAt)}</td>
                <td className="syslog-col-sev">
                  <span className={`syslog-badge syslog-badge--${sev}`}>{it.severity}</span>
                </td>
                <td className="syslog-col-source">{it.sourceAgent || '-'}</td>
                <td className="syslog-col-code">
                  <code>{it.eventCode || '-'}</code>
                </td>
                <td className="syslog-col-summary" title={it.summary}>
                  {it.summary}
                </td>
                <td className="syslog-col-ack">
                  {it.acked ? (
                    <span className="syslog-ack-dot syslog-ack-dot--on" title="읽음" />
                  ) : (
                    <span className="syslog-ack-dot" title="미확인" />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function formatTs(iso: string): string {
  try {
    return new Date(iso).toLocaleString('ko-KR', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: false,
    });
  } catch {
    return iso;
  }
}
